/**
 * useBlockState - Block State Management Hook
 *
 * Manages block-based content for Rich Block Engine integration.
 * Supports normalized block model, streaming updates, and block operations.
 *
 * @module hooks/useBlockState
 */

import { useState, useCallback, useMemo } from 'react';

/**
 * Block Types
 */
export const BlockType = {
  PARAGRAPH: 'paragraph',
  HEADING: 'heading',
  BULLET: 'bullet',
  NUMBERED: 'numbered',
  QUOTE: 'quote',
  CODE: 'code',
  CITATION: 'citation',
  FILE_REFERENCE: 'file_reference',
  TABLE: 'table',
  IMAGE: 'image',
  DIVIDER: 'divider',
};

/**
 * Generate unique block ID
 */
const generateBlockId = () => `block_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

/**
 * Create a new block
 *
 * @param {string} type - Block type
 * @param {string} content - Block content
 * @param {Object} metadata - Optional metadata
 * @returns {Object} New block object
 */
export const createBlock = (type, content, metadata = {}) => ({
  id: generateBlockId(),
  type,
  content,
  metadata,
  createdAt: Date.now(),
  updatedAt: Date.now(),
});

/**
 * useBlockState Hook
 *
 * @param {Array} initialBlocks - Initial blocks array
 * @returns {Object} Block state and operations
 */
export function useBlockState(initialBlocks = []) {
  const [blocks, setBlocks] = useState(initialBlocks);
  const [streamingBlockId, setStreamingBlockId] = useState(null);

  // Add block at end or specific index
  const addBlock = useCallback((type, content, metadata = {}, index = -1) => {
    const newBlock = createBlock(type, content, metadata);

    setBlocks(prev => {
      if (index === -1 || index >= prev.length) {
        return [...prev, newBlock];
      }
      const updated = [...prev];
      updated.splice(index, 0, newBlock);
      return updated;
    });

    return newBlock.id;
  }, []);

  // Update existing block
  const updateBlock = useCallback((blockId, updates) => {
    setBlocks(prev =>
      prev.map(block =>
        block.id === blockId ? { ...block, ...updates, updatedAt: Date.now() } : block
      )
    );
  }, []);

  // Delete block
  const deleteBlock = useCallback(blockId => {
    setBlocks(prev => prev.filter(block => block.id !== blockId));
  }, []);

  // Merge two blocks
  const mergeBlocks = useCallback((blockId1, blockId2) => {
    setBlocks(prev => {
      const idx1 = prev.findIndex(b => b.id === blockId1);
      const idx2 = prev.findIndex(b => b.id === blockId2);

      if (idx1 === -1 || idx2 === -1) return prev;

      const [first, second] = idx1 < idx2 ? [prev[idx1], prev[idx2]] : [prev[idx2], prev[idx1]];
      const merged = {
        ...first,
        content: first.content + '\n' + second.content,
        updatedAt: Date.now(),
      };

      return prev.map(b => (b.id === first.id ? merged : b)).filter(b => b.id !== second.id);
    });
  }, []);

  // Split block at cursor position
  const splitBlock = useCallback((blockId, cursorPosition) => {
    setBlocks(prev => {
      const idx = prev.findIndex(b => b.id === blockId);
      if (idx === -1) return prev;

      const block = prev[idx];
      const content1 = block.content.slice(0, cursorPosition);
      const content2 = block.content.slice(cursorPosition);

      const newBlock = createBlock(block.type, content2, { ...block.metadata });

      const updated = [...prev];
      updated[idx] = { ...block, content: content1, updatedAt: Date.now() };
      updated.splice(idx + 1, 0, newBlock);

      return updated;
    });
  }, []);

  // Reorder blocks
  const reorderBlocks = useCallback((fromIndex, toIndex) => {
    setBlocks(prev => {
      const updated = [...prev];
      const [removed] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, removed);
      return updated;
    });
  }, []);

  // Start streaming to a new block
  const startStreamingBlock = useCallback((type = BlockType.PARAGRAPH, metadata = {}) => {
    const newBlock = createBlock(type, '', { ...metadata, isStreaming: true });
    setBlocks(prev => [...prev, newBlock]);
    setStreamingBlockId(newBlock.id);
    return newBlock.id;
  }, []);

  // Append content to streaming block
  const appendToStreamingBlock = useCallback(
    content => {
      if (!streamingBlockId) return;

      setBlocks(prev =>
        prev.map(block =>
          block.id === streamingBlockId
            ? { ...block, content: block.content + content, updatedAt: Date.now() }
            : block
        )
      );
    },
    [streamingBlockId]
  );

  // Finish streaming block
  const finishStreamingBlock = useCallback(() => {
    if (!streamingBlockId) return;

    setBlocks(prev =>
      prev.map(block =>
        block.id === streamingBlockId
          ? {
              ...block,
              metadata: { ...block.metadata, isStreaming: false },
              updatedAt: Date.now(),
            }
          : block
      )
    );
    setStreamingBlockId(null);
  }, [streamingBlockId]);

  // Clear all blocks
  const clearBlocks = useCallback(() => {
    setBlocks([]);
    setStreamingBlockId(null);
  }, []);

  // Get block by ID
  const getBlock = useCallback(
    blockId => {
      return blocks.find(b => b.id === blockId) || null;
    },
    [blocks]
  );

  // Computed: block count
  const blockCount = useMemo(() => blocks.length, [blocks]);

  // Computed: is currently streaming
  const isStreaming = useMemo(() => !!streamingBlockId, [streamingBlockId]);

  return {
    // State
    blocks,
    blockCount,
    streamingBlockId,
    isStreaming,

    // CRUD Operations
    addBlock,
    updateBlock,
    deleteBlock,
    getBlock,
    clearBlocks,

    // Block manipulation
    mergeBlocks,
    splitBlock,
    reorderBlocks,

    // Streaming operations
    startStreamingBlock,
    appendToStreamingBlock,
    finishStreamingBlock,

    // Direct setter for bulk updates
    setBlocks,
  };
}

export default useBlockState;
